import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import {
  ILiveEventFragmentFragment,
  IPaywallFragmentFragment,
} from '~Api/Graphql/gql/types.generated';
import apiConfig from '~Configs/apiConfig';
import useMember, {
  useSelecteSubscription,
  useSubscriptionLoaded,
} from '@src/Store/selectors/useMember';
import useDefaultPaywall from '@src/Store/selectors/useDefaultPaywall';
import DateFactory from '@src/tools/DateFactory';
import {useAppSelector} from '@src/Store/hooks';
import {UmbTypeChecker} from '@src/Umbraco/Utils/UmbTypeChecker';
import useIsVodacomUser from '@src/Hooks/useIsVodacomUser';
import useIsMtnUser from '@src/Hooks/useIsMtnUser';
import {MTN_SUBSCRIBE_URL} from '@src/Configs/mtnConfig';
import {auth} from '@src/firebase';
import {Routes} from '@src/Constant/routes';
import useAppNavigation from '@src/utils/useAppNavigation';
import {IMemberSubscription} from '@src/Store/Slices/Member.slice';
import {IPromocodeResponse} from '@src/Api/TraceApi/endpoints/memberDataEndpoint/memberPromocode.endpoint';

export type AccessType = ILiveEventFragmentFragment['accessType'];

const PLAY_TRACE_URL = 'https://play.trace.plus';

export interface IPaywallSetting {
  text: string;
  canPreview: boolean;
  previewDurationTv: number;
  previewDurationVod: number;
  isDefaultPaywall?: boolean;
  currentPaywall?: IPaywallFragmentFragment;
  title?: string;
  subtitle?: string;
  animatedTitle?: string[];
  coverMobile?: string;
  coverDesktop?: string;
  labelCta?: string;
  cancelMention?: string;
  mentionFooter?: string;
  animationDuration?: number;
  highlightedSubtitle?: string;
  icon?: string;
}

export interface ContentAccessRequest {
  accessType?: AccessType;
  paywall?: IPaywallFragmentFragment;
  title?: string;
  navigateTo?: {route: string; state?: any};
}

interface PaywallContextType {
  subscription: IMemberSubscription | undefined;
  subscriptionLoaded: boolean;
  isVodacom: boolean;
  isVodacomFreeUser: boolean;
  isMtn: boolean;
  isMtnFreeUser: boolean;
  isValidPromocode: boolean;
  defaultPaywall?: IPaywallFragmentFragment;
  memberPromoCode?: IPromocodeResponse;

  canSeeContent: (accessType?: AccessType) => boolean;
  canPreview: (paywall?: IPaywallFragmentFragment) => boolean;
  isLocked: (accessType?: AccessType) => boolean;
  isMemberFreemium: () => boolean;
  getPremiumPreviewSettings: (
    paywall?: IPaywallFragmentFragment,
  ) => IPaywallSetting;

  requestAccess: (content: ContentAccessRequest) => void;
  vodacomRedirectToPaywall: () => Promise<void>;
  mtnRedirectToPaywall: () => void;
}

const PaywallContext = createContext<PaywallContextType | undefined>(undefined);

export const usePaywallAccess = (): PaywallContextType => {
  const context = useContext(PaywallContext);
  if (!context) {
    throw new Error('usePaywallAccess must be used within PaywallProvider');
  }
  return context;
};

interface PaywallProviderProps {
  children: ReactNode;
}

const PaywallProvider: React.FC<PaywallProviderProps> = ({children}) => {
  const memberSubscription = useSelecteSubscription();
  const subscriptionLoaded = useSubscriptionLoaded();
  const defaultPaywall = useDefaultPaywall();
  const memberPromoCode = useAppSelector(({member}) => member.memberPromoCode);
  const isVodacom = useIsVodacomUser();
  const isMtn = useIsMtnUser();
  const member = useMember();
  const navigation = useAppNavigation();

  const isValidPromocode = useMemo(
    () =>
      !!memberPromoCode &&
      (!memberPromoCode?.subscriptionEnd ||
        new DateFactory(memberPromoCode.subscriptionEnd).diffDate() < 0),
    [memberPromoCode],
  );

  const isVodacomFreeUser = useMemo(() => {
    if (!isVodacom || !member) return false;
    if (!subscriptionLoaded) return false;
    if (!memberSubscription) return true;
    if (memberSubscription.productId === apiConfig.freemiumProductId) return true;
    if (memberSubscription.subscriptionEnd) {
      return new Date(memberSubscription.subscriptionEnd) < new Date();
    }
    return false;
  }, [isVodacom, member, memberSubscription, subscriptionLoaded]);

  // Same rule as Vodacom: inactive MTN users keep the MTN subscription row
  // with a past subscriptionEnd.
  const isMtnFreeUser = useMemo(() => {
    if (!isMtn || !member) return false;
    if (!subscriptionLoaded) return false;
    if (!memberSubscription) return true;
    if (memberSubscription.productId === apiConfig.freemiumProductId) return true;
    if (memberSubscription.subscriptionEnd) {
      return new Date(memberSubscription.subscriptionEnd) < new Date();
    }
    return false;
  }, [isMtn, member, memberSubscription, subscriptionLoaded]);

  const isCarrierFreeUser = isVodacomFreeUser || isMtnFreeUser;

  const isMemberFreemium = useCallback(
    (): boolean =>
      !isValidPromocode &&
      (!memberSubscription?.productId ||
        memberSubscription.productId === apiConfig.freemiumProductId),
    [isValidPromocode, memberSubscription],
  );

  const canSeeContent = useCallback(
    (accessType?: AccessType) => {
      if (isValidPromocode) return true;

      if (isCarrierFreeUser && accessType && accessType.length > 0) {
        const hasFreemium = !!accessType.find(
          e =>
            UmbTypeChecker.isType(e, 'Subscription') &&
            e?.productID === apiConfig.freemiumProductId,
        );
        if (!hasFreemium) return false;
      }

      if (memberSubscription) {
        if (accessType && accessType.length > 0) {
          const hasFreemium = !!accessType.find(
            e =>
              UmbTypeChecker.isType(e, 'Subscription') &&
              e?.productID === apiConfig.freemiumProductId,
          );
          if (!hasFreemium) {
            return (
              memberSubscription.productId !== apiConfig.freemiumProductId
            );
          }
        }
      }
      return true;
    },
    [memberSubscription, isValidPromocode, isCarrierFreeUser],
  );

  const canPreview = useCallback(
    (paywall?: IPaywallFragmentFragment): boolean =>
      isCarrierFreeUser ? false : !(paywall ?? defaultPaywall)?.disabledPreview,
    [defaultPaywall, isCarrierFreeUser],
  );

  const isLocked = useCallback(
    (accessType?: AccessType): boolean => !canSeeContent(accessType),
    [canSeeContent],
  );

  const getPremiumPreviewSettings = useCallback(
    (
      paywall: IPaywallFragmentFragment | undefined = defaultPaywall,
    ): IPaywallSetting => {
      const currentPaywall = paywall || defaultPaywall;
      const timeRatio = 60;
      return {
        text: currentPaywall?.playerPremiumText || '',
        canPreview: isCarrierFreeUser
          ? false
          : !currentPaywall?.disabledPreview,
        previewDurationTv: (currentPaywall?.previewDurationTv || 0) * timeRatio,
        previewDurationVod:
          (currentPaywall?.previewDurationVod || 0) * timeRatio,
        currentPaywall,
        title: currentPaywall?.previewTitle,
        subtitle: currentPaywall?.previewSubtitle,
        animatedTitle: currentPaywall?.previewAnimatedTitle?.map(e => e || ''),
        coverMobile: currentPaywall?.previewCoverMobile?.url,
        coverDesktop: currentPaywall?.previewCoverDesktop?.url,
        labelCta: currentPaywall?.previewLabelCta,
        cancelMention: currentPaywall?.previewCancelMention,
        mentionFooter: currentPaywall?.previewMentionFooter,
        animationDuration: currentPaywall?.previewAnimationDuration,
        highlightedSubtitle: currentPaywall?.previewHighlightedSubtitle,
        icon: currentPaywall?.previewIcon?.url,
        isDefaultPaywall: !paywall,
      };
    },
    [defaultPaywall, isCarrierFreeUser],
  );

  const mtnRedirectToPaywall = useCallback(() => {
    window.location.href = MTN_SUBSCRIBE_URL;
  }, []);

  const vodacomRedirectToPaywall = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        let tokenResult = await user.getIdTokenResult(false);
        let eMsisdn = (tokenResult.claims?.vodacomEncryptedMsisdn ||
          tokenResult.claims?.vodacomEmsisdn) as string | undefined;
        if (!eMsisdn) {
          tokenResult = await user.getIdTokenResult(true);
          eMsisdn = (tokenResult.claims?.vodacomEncryptedMsisdn ||
            tokenResult.claims?.vodacomEmsisdn) as string | undefined;
        }
        if (eMsisdn) {
          window.location.href = `${PLAY_TRACE_URL}?e-msisdn=${eMsisdn}`;
          return;
        }
      }
    } catch {
      // best-effort
    }
    window.location.href = PLAY_TRACE_URL;
  }, []);

  const requestAccess = useCallback(
    (content: ContentAccessRequest) => {
      const canSee = canSeeContent(content.accessType);
      const preview = canPreview(content.paywall);

      if (canSee || preview) {
        if (content.navigateTo) {
          navigation.navigate(content.navigateTo.route, content.navigateTo.state);
        }
        return;
      }

      if (isVodacomFreeUser) {
        vodacomRedirectToPaywall();
        return;
      }

      if (isMtnFreeUser) {
        mtnRedirectToPaywall();
        return;
      }

      navigation.navigate(Routes.PAYWALL, {
        state: {
          params: {
            id: content.paywall?.id || 'default',
            headerTitle: content.title || '',
            paywall: content.paywall,
          },
        },
      });
    },
    [
      canSeeContent,
      canPreview,
      isVodacomFreeUser,
      vodacomRedirectToPaywall,
      isMtnFreeUser,
      mtnRedirectToPaywall,
      navigation,
    ],
  );

  const value = useMemo<PaywallContextType>(
    () => ({
      subscription: memberSubscription,
      subscriptionLoaded,
      isVodacom,
      isVodacomFreeUser,
      isMtn,
      isMtnFreeUser,
      isValidPromocode,
      defaultPaywall,
      memberPromoCode,
      canSeeContent,
      canPreview,
      isLocked,
      isMemberFreemium,
      getPremiumPreviewSettings,
      requestAccess,
      vodacomRedirectToPaywall,
      mtnRedirectToPaywall,
    }),
    [
      memberSubscription,
      subscriptionLoaded,
      isVodacom,
      isVodacomFreeUser,
      isMtn,
      isMtnFreeUser,
      isValidPromocode,
      defaultPaywall,
      memberPromoCode,
      canSeeContent,
      canPreview,
      isLocked,
      isMemberFreemium,
      getPremiumPreviewSettings,
      requestAccess,
      vodacomRedirectToPaywall,
      mtnRedirectToPaywall,
    ],
  );

  return (
    <PaywallContext.Provider value={value}>{children}</PaywallContext.Provider>
  );
};

export default PaywallProvider;
